import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { logout } from '../../firebase/auth';
import LoginPrompt from '../../Auth/LoginPrompt';
import { useToast } from '../Toast';
import { useConfirm } from '../common/ConfirmDialog';
import { Sheet } from '../common/Dialog';
import { getCurrentSeason, getMaxDiscountLabel } from '../../config/seasonConfig';
import useIsMobile from '../../hooks/useIsMobile';
import useHasUnseenWhatsNew from '../../hooks/useHasUnseenWhatsNew';
import { openWhatsNew } from '../../config/whatsNew';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getPartnerStatus } from '../../api/partnerApi';
import { getMyOrganization } from '../../api/organizationApi';
// 📊 이벤트 트래킹
import {
  trackDiscountBubbleClick,
  trackDiscountBubbleImpression,
  trackGroup50Event,
  trackPro50Event,
} from '../../utils/eventTracker';
import { isDiscountBubbleTime } from '../../utils/discountBubbleTiming';
import {
  GROUP50_LANDING_PATH,
  hasSeenGroup50Entry,
  isGroup50EventActive,
  markGroup50EntrySeen,
} from '../../utils/group50Campaign';
import {
  PRO50_LANDING_PATH,
  hasSeenPro50Entry,
  isPro50Active,
  markPro50EntrySeen,
} from '../../utils/pro50Campaign';

// 메뉴 도트 — 단체 50% 이벤트(모바일 햄버거·프로필 "이벤트")와 새 소식(프로필 버튼·"새 소식")에 쓴다.
const group50DotStyle = {
  display: 'inline-block',
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: 'var(--wq-accent-fill, #3182F6)',
  flexShrink: 0,
};

const conversationMenuItemStyle = {
  width: '100%',
  minHeight: '44px',
  padding: '10px 12px',
  border: 'none',
  borderRadius: '8px',
  background: 'transparent',
  color: 'var(--wq-text, #333d4b)',
  fontSize: '14px',
  textAlign: 'left',
  cursor: 'pointer',
};

// 모바일 바텀시트(Sheet) 안의 메뉴 항목 — 터치 타겟 52px, 본문 16px
const conversationSheetItemStyle = {
  ...conversationMenuItemStyle,
  minHeight: '52px',
  padding: '12px',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: 500,
};

// 시트 제목은 대화 제목 한 줄로 — 길면 말줄임 (접근성 이름은 전체 제목 유지)
const conversationSheetTitleStyle = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const ConversationListItem = React.memo(({
  chat,
  isActive,
  isHovered,
  isEditing,
  isMenuOpen,
  isPcMode,
  editingTitle,
  renameInputRef,
  onHoverChange,
  onSelect,
  onEditingTitleChange,
  onFinishRename,
  onRenameKeyDown,
  onToggleMenu,
}) => (
  <div
    onMouseEnter={() => onHoverChange(chat.id)}
    onMouseLeave={() => onHoverChange(null)}
    style={{
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '4px',
      cursor: 'pointer',
      backgroundColor: isActive ? 'var(--wq-surface-muted, #f1f5f9)' : isHovered ? 'var(--wq-surface-subtle, #f8fafc)' : 'transparent',
      transition: 'background-color 0.2s ease',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      position: 'relative',
    }}
  >
    {!isEditing && (
      <button
        type="button"
        aria-label={`${chat.title} 대화 열기`}
        aria-current={isActive ? 'page' : undefined}
        onClick={() => onSelect(chat.id)}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          width: '100%',
          border: 'none',
          borderRadius: '8px',
          background: 'transparent',
          cursor: 'pointer',
        }}
      />
    )}
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      paddingRight: isPcMode ? '36px' : '44px',
      position: 'relative',
      zIndex: 1,
      pointerEvents: isEditing ? 'auto' : 'none',
    }}>
      {chat.pinned && (
        <svg
          aria-label="고정된 대화"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#3182F6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <path d="M12 17v5"></path>
          <path d="M5 17h14"></path>
          <path d="M6 3h12l-2 8 3 3H5l3-3-2-8z"></path>
        </svg>
      )}
      {isEditing ? (
        <input
          ref={renameInputRef}
          value={editingTitle}
          maxLength={60}
          aria-label="대화 이름"
          onChange={(e) => onEditingTitleChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={onFinishRename}
          onKeyDown={onRenameKeyDown}
          style={{
            width: '100%',
            minWidth: 0,
            height: isPcMode ? '34px' : '44px',
            padding: '6px 8px',
            border: '1px solid #3182F6',
            borderRadius: '8px',
            outline: 'none',
            boxShadow: '0 0 0 3px #e8f3ff',
            color: 'var(--wq-text-strong, #191f28)',
            fontSize: isPcMode ? '14px' : '16px',
          }}
        />
      ) : (
        <div style={{
          minWidth: 0,
          fontSize: '14px',
          fontWeight: isActive ? '600' : '400',
          color: 'var(--wq-text, #334155)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {chat.title}
        </div>
      )}
    </div>
    <div style={{ position: 'relative', zIndex: 1, pointerEvents: 'none', fontSize: '12px', color: 'var(--wq-text-faint, #94a3b8)' }}>
      {chat.date}
    </div>
    
    {/* 대화 액션은 하나의 메뉴로 단순화 */}
    {(!isPcMode || isHovered || isMenuOpen) && (
      <button
        className="nav-hover-bg nav-hover-bg--gray"
        data-chat-menu
        onClick={(e) => onToggleMenu(e, chat.id)}
        style={{
          position: 'absolute',
          right: '4px',
          top: '8px',
          zIndex: 2,
          width: '44px',
          height: '44px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          borderRadius: '8px',
          color: 'var(--wq-text-sub, #6b7684)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.15s ease',
        }}
        aria-label={`${chat.title} 메뉴 열기`}
        aria-expanded={isMenuOpen}
      >
        <span style={{ fontSize: '20px', lineHeight: 1, transform: 'translateY(-2px)' }}>…</span>
      </button>
    )}
  </div>
));

const Navigation = () => {
  const { currentUser, userDetails, isAnonymous } = useAuth();
  const {
    conversations,
    currentConversation,
    deleteConversation,
    setConversationPinned,
    renameConversation,
  } = useChat();
  const { subscriptionStatus, getRemainingDays } = useSubscription();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const discountBubbleImpressionRef = useRef(new Set());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTrainingPartner, setIsTrainingPartner] = useState(false);
  const [isSchoolAdmin, setIsSchoolAdmin] = useState(false);
  const [schoolReviewTasks, setSchoolReviewTasks] = useState(0);
  const schoolAdminCheckedUidRef = useRef(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation(); // 현재 경로 확인을 위해 추가
  const userMenuRef = useRef(null);
  const userButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileButtonRef = useRef(null);

  const currentSeason = useMemo(() => getCurrentSeason(), []);
  const seasonShortLabel = currentSeason.headlineSuffix || `${currentSeason.label}, 지금이 가장 저렴해요`;
  const seasonLongLabel = `${currentSeason.urgencyText} · ${currentSeason.deadlineLabel}`;
  const _maxDiscountLabel = useMemo(() => getMaxDiscountLabel(), []);
  const _maxDiscountCta = `${_maxDiscountLabel} 받기 →`;
  
  // 로그인 팝업 상태
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // PRO 만료 임박 말풍선 상태
  const [showProBubble, setShowProBubble] = useState(false);
  const [isTrialUser, setIsTrialUser] = useState(false); // 체험 사용자 여부
  const [proBubbleOffer, setProBubbleOffer] = useState(null);
  
  // 새학기 이벤트 말풍선 상태
  const [showEventBubble, setShowEventBubble] = useState(false);
  
  // PC 모드 여부 (모바일 기준인 768px 초과)
  const isPcMode = !isMobile;

  // 단체 50% 이벤트 상시 진입점 — 팝업을 닫은 사용자가 사이드바 "요금제" 필로
  // 다시 찾아갈 수 있게 한다. 프로필 "이벤트"는 허브(/events)로 보낸다.
  // 10월에는 PRO 최대 50%(인원 제한 없음)가 같은 자리를 이어받는다.
  const isPro50EventOn = isPro50Active();
  const isGroup50Active = !isPro50EventOn && isGroup50EventActive();
  const [group50EntrySeen, setGroup50EntrySeen] = useState(hasSeenGroup50Entry);
  const [pro50EntrySeen, setPro50EntrySeen] = useState(hasSeenPro50Entry);
  useEffect(() => {
    // 랜딩에 도착한 순간 도트를 끈다 (Group50EventPage의 마운트 effect와 순서 무관하게 보장).
    if (location.pathname === GROUP50_LANDING_PATH) markGroup50EntrySeen();
    if (location.pathname === PRO50_LANDING_PATH) markPro50EntrySeen();
    setGroup50EntrySeen(hasSeenGroup50Entry());
    setPro50EntrySeen(hasSeenPro50Entry());
  }, [location.pathname]);
  const showGroup50Dot = isPro50EventOn
    ? !pro50EntrySeen
    : isGroup50Active && !group50EntrySeen;
  // 새 소식(업데이트) — 못 본 소식이 있으면 프로필 버튼과 메뉴 "새 소식"에 도트
  const hasUnseenWhatsNew = useHasUnseenWhatsNew(currentUser?.uid, currentUser?.metadata?.creationTime);
  
  // 현재 경로가 /chat인지 확인 (대화 목록 서브패널은 /chat 자체에서만)
  const isChatPage = location.pathname === '/chat';
  // AI 채팅 섹션(채팅 + AI 맞춤 설정) 안에 있는지 — 상위 메뉴 활성 표시와 하위 항목 노출에 사용
  const isChatSettingsPage = location.pathname === '/chat/settings';
  const isChatSection = isChatPage || isChatSettingsPage;
  
  // 하드코딩된 대화 기록 대신 ChatContext에서 가져온 데이터 사용
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [activeChatMenuId, setActiveChatMenuId] = useState(null);
  const [desktopMenuPosition, setDesktopMenuPosition] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const renameInputRef = useRef(null);
  const renameSubmittingRef = useRef(false);
  
  // 검색 기능 관련 상태 추가
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredChatHistory, setFilteredChatHistory] = useState([]);
  const searchInputRef = useRef(null);

  // 검색어 변경 시 대화 목록 필터링
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredChatHistory(conversations);
    } else {
      const filtered = conversations.filter(chat => 
        chat.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredChatHistory(filtered);
    }
  }, [searchQuery, conversations]);

  useEffect(() => {
    let cancelled = false;
    setIsTrainingPartner(false);
    if (!currentUser || currentUser.isTempReviewUser) {
      return () => {
        cancelled = true;
      };
    }

    getPartnerStatus(currentUser)
      .then((result) => {
        if (!cancelled && result.is_active === true) {
          setIsTrainingPartner(true);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // PRO 만료 임박 말풍선 표시 여부 결정
  useEffect(() => {
    let cancelled = false;

    const hideBubble = () => {
      if (cancelled) return;
      setShowProBubble(false);
      setIsTrialUser(false);
      setProBubbleOffer(null);
    };

    const resolveBubbleOffer = async () => {
      if (!currentUser || !subscriptionStatus) {
        hideBubble();
        return;
      }

      const remainingDays = getRemainingDays();
      const isPro = subscriptionStatus.isPro || false;
      const isActive = subscriptionStatus.status === 'active';
      const subscriptionType = subscriptionStatus.type || 'free';
      const isTrial = subscriptionType === 'pro_trial';

      if (!(isPro && isActive && isDiscountBubbleTime({ isTrialOffer: isTrial, remainingDays }))) {
        hideBubble();
        return;
      }

      const storageKey = isTrial ? 'trialBubbleDismissedUntil' : 'proBubbleDismissedUntil';
      const dismissedUntil = localStorage.getItem(storageKey);
      if (dismissedUntil && new Date() < new Date(dismissedUntil)) {
        hideBubble();
        return;
      }

      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(`${getApiBaseUrl()}/api/payment/my-offers`, {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (!response.ok) throw new Error(`my-offers ${response.status}`);
        const result = await response.json();
        // 서버가 준 오퍼를 그대로 따른다. 이벤트 체험 쿠폰(SPRING14)으로 시작한 PRO는
        // type이 'pro'지만 서버가 trial50 자격을 주므로 trial50을 먼저 찾는다.
        const offers = result.offers || [];
        const matchedOffer = offers.find((offer) => offer.id === 'trial50')
          || offers.find((offer) => offer.id === 'renewal20');
        if (!matchedOffer) {
          hideBubble();
          return;
        }
        const expectedOfferId = matchedOffer.id;
        const isTrialOffer = isTrial || matchedOffer.id === 'trial50';
        // 이벤트 체험 쿠폰 PRO 는 type 이 'pro' 라 오퍼를 받은 뒤에야 체험 기준을 적용할 수 있다.
        if (!isDiscountBubbleTime({ isTrialOffer, remainingDays })) {
          hideBubble();
          return;
        }

        if (!cancelled) {
          setProBubbleOffer(matchedOffer);
          setShowProBubble(true);
          setIsTrialUser(isTrialOffer);
          const impressionKey = [
            'whatquiz_discount_bubble_impression',
            currentUser.uid,
            matchedOffer.id,
          ].join(':');
          let alreadyTracked = discountBubbleImpressionRef.current.has(
            impressionKey,
          );
          try {
            alreadyTracked = alreadyTracked
              || sessionStorage.getItem(impressionKey) === '1';
          } catch (_) {
            // sessionStorage가 막혀도 메모리 가드로 현재 마운트 중복을 막는다.
          }
          if (!alreadyTracked) {
            trackDiscountBubbleImpression(
              matchedOffer.promo || expectedOfferId,
              remainingDays,
            );
            discountBubbleImpressionRef.current.add(impressionKey);
            try {
              sessionStorage.setItem(impressionKey, '1');
            } catch (_) {
              // 저장소가 막혀도 사용자 흐름과 말풍선 노출은 유지한다.
            }
          }
        }
      } catch (offerError) {
        console.error('할인 말풍선 자격 확인 실패:', offerError);
        hideBubble();
      }
    };

    // 사용자/구독 상태가 바뀐 순간 이전 사용자의 오퍼가 잠깐 남지 않게 먼저 숨긴다.
    hideBubble();
    resolveBubbleOffer();
    return () => {
      cancelled = true;
    };
  }, [currentUser, subscriptionStatus, getRemainingDays]);

  // 시즌 이벤트 말풍선은 사전예약 퍼널 임시 종료 동안 노출하지 않는다.
  useEffect(() => {
    setShowEventBubble(false);
  }, [location.pathname]);

  // 말풍선 닫기 (3일간 숨김 / 체험은 1일간 숨김)
  const handleDismissProBubble = (e) => {
    e.stopPropagation();
    const dismissUntil = new Date();
    dismissUntil.setDate(dismissUntil.getDate() + (isTrialUser ? 1 : 3));
    const storageKey = isTrialUser ? 'trialBubbleDismissedUntil' : 'proBubbleDismissedUntil';
    localStorage.setItem(storageKey, dismissUntil.toISOString());
    setShowProBubble(false);
    showToast(
      '할인 혜택은 마이페이지에서 언제든 다시 볼 수 있어요.',
      'info',
      6000
    );
  };

  // 이벤트 말풍선 닫기 (7일간 숨김)
  const handleDismissEventBubble = (e) => {
    e.stopPropagation();
    const dismissUntil = new Date();
    dismissUntil.setDate(dismissUntil.getDate() + 7);
    localStorage.setItem('eventBubbleDismissedUntil', dismissUntil.toISOString());
    setShowEventBubble(false);
  };

  const handleEventBubbleClick = (e) => {
    e.stopPropagation();
    setShowEventBubble(false);
    setIsUserMenuOpen(false);
    navigate('/pricing');
  };

  // 말풍선 메인 CTA 클릭 시 결제 페이지로 이동
  // 체험 사용자: 50% 할인 (trial50 프로모)
  // 일반 PRO: 20% 할인 (renewal20 프로모)
  const handleProBubbleClick = () => {
    const remainingDays = getRemainingDays();
    const discountType = proBubbleOffer?.promo || (isTrialUser ? 'trial50' : 'renewal20');
    const planKey = proBubbleOffer?.recommended_plan || '3month';
    
    // 📊 할인 말풍선 클릭 이벤트 트래킹
    trackDiscountBubbleClick(discountType, remainingDays);
    
    setShowProBubble(false);
    navigate(
      `/subscription/pro?plan=${planKey}&promo=${discountType}&source=discount_bubble`
    );
  };


  // 초기 대화 목록 설정
  useEffect(() => {
    setFilteredChatHistory(conversations);
  }, [conversations]);

  // 검색 입력창 포커스 관리
  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  // 외부 클릭 시 검색창 닫기
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showSearchInput && 
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target) &&
        !event.target.closest('.search-button')
      ) {
        setShowSearchInput(false);
        setSearchQuery('');
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchInput]);

  useEffect(() => {
    if (!activeChatMenuId || !isPcMode) return undefined;
    const closeMenu = (event) => {
      if (!event.target.closest('[data-chat-menu]')) {
        setActiveChatMenuId(null);
        setDesktopMenuPosition(null);
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [activeChatMenuId, isPcMode]);
  
  // 모바일 메뉴 바깥 클릭 감지
  useEffect(() => {
    // 페이지 내의 빈 공간 클릭 시 모바일 네비게이션 메뉴 닫기
    const handleDocumentClick = (e) => {
      if (!isPcMode && isMenuOpen) {
        // 네비게이션과 햄버거 버튼을 제외한 영역 클릭 시
        const clickedInsideNav = mobileMenuRef.current?.contains(e.target);
        const clickedOnButton = mobileButtonRef.current?.contains(e.target);
        
        if (!clickedInsideNav && !clickedOnButton) {
          setIsMenuOpen(false);
        }
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [isPcMode, isMenuOpen]);
  
  // 외부 클릭 감지 핸들러
  useEffect(() => {
    function handleClickOutside(event) {
      // 사용자 메뉴가 열려있고, 클릭이 메뉴나 버튼 외부에서 발생했을 때
      if (
        isUserMenuOpen && 
        userMenuRef.current && 
        !userMenuRef.current.contains(event.target) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(event.target)
      ) {
        setIsUserMenuOpen(false);
      }
      
      // 모바일 메뉴가 열려있고, 클릭이 메뉴나 버튼 외부에서 발생했을 때
      if (
        !isPcMode &&
        isMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }
    
    // 전역 클릭 이벤트 추가
    document.addEventListener('mousedown', handleClickOutside);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isMenuOpen, isPcMode]);

  useEffect(() => {
    setIsSchoolAdmin(false);
    setSchoolReviewTasks(0);
    schoolAdminCheckedUidRef.current = null;
  }, [currentUser?.uid]);

  // 학교 관리자는 극소수라 모든 화면에서 묻지 않고, 프로필 메뉴를 처음 열 때 한 번만 확인한다.
  useEffect(() => {
    const uid = currentUser?.uid;
    if (!isUserMenuOpen || !uid || currentUser?.isTempReviewUser) return;
    if (schoolAdminCheckedUidRef.current === uid) return;
    schoolAdminCheckedUidRef.current = uid;
    getMyOrganization(currentUser)
      .then((res) => {
        if (schoolAdminCheckedUidRef.current !== uid) return;
        setIsSchoolAdmin(res?.organization?.role === 'admin');
        setSchoolReviewTasks(Number(res?.organization?.review_tasks) || 0);
      })
      .catch(() => {
        if (schoolAdminCheckedUidRef.current === uid) schoolAdminCheckedUidRef.current = null;
      });
  }, [isUserMenuOpen, currentUser]);

  // 외부에서 메뉴를 닫을 수 있는 메서드 추가
  useEffect(() => {
    // 현재 ref 값을 변수에 저장하여 cleanup 함수에서 안전하게 접근
    const currentMobileButtonRef = mobileButtonRef.current;
    
    if (currentMobileButtonRef) {
      // 모바일 메뉴 버튼에 닫기 메서드 추가
      currentMobileButtonRef.__closeMenu = () => {
        setIsMenuOpen(false);
      };
    }
    
    return () => {
      if (currentMobileButtonRef) {
        delete currentMobileButtonRef.__closeMenu;
      }
    };
  }, []);

  useEffect(() => {
    const menu = mobileMenuRef.current;
    if (!menu) return;

    if (!isPcMode && !isMenuOpen) {
      menu.setAttribute('inert', '');
    } else {
      menu.removeAttribute('inert');
    }
  }, [isMenuOpen, isPcMode]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      // 오류 발생 시 조용히 처리
    }
  };

  const handleGoogleLogin = () => {
    // Google 로그인을 AuthPage로 리다이렉트 (현재 경로를 redirect 파라미터로 전달)
    const currentPath = location.pathname + location.search;
    const redirectParam = encodeURIComponent(currentPath);
    navigate(`/auth?tab=login&redirect=${redirectParam}`);
  };

  // 대화 선택 함수
  const handleChatSelect = (chatId) => {
    setActiveChatMenuId(null);
    setDesktopMenuPosition(null);
    // currentConversation 설정은 ChatPage에서 URL 파라미터를 통해 처리
    
    // 모바일 환경에서 메뉴 닫기
    if (!isPcMode) {
      setIsMenuOpen(false);
    }
    
    // 채팅 페이지로 이동 (대화 ID를 URL 파라미터로 전달)
    navigate(`/chat?id=${chatId}`);
  };

  // 대화 삭제 함수
  const handleDeleteConversation = async (e, chatId) => {
    e.stopPropagation(); // 대화 선택 이벤트 전파 방지
    setActiveChatMenuId(null);
    setDesktopMenuPosition(null);
    
    // 서버에서 지워지는 삭제라 실행 취소 대신 확인 대화상자를 쓴다
    const ok = await confirmDialog({
      title: '이 대화를 삭제할까요?',
      description: '삭제한 대화는 되돌릴 수 없어요',
      confirmLabel: '삭제하기',
      danger: true,
    });
    if (!ok) return;

    try {
      await deleteConversation(chatId);

      // 삭제한 대화가 현재 선택된 대화인 경우 새 채팅으로 이동
      if (currentConversation === chatId) {
        navigate('/chat?new=true');
      }
    } catch (error) {
      console.error('대화 삭제 실패:', error);
      showToast('대화를 삭제하지 못했어요. 다시 시도해주세요', 'error');
    }
  };

  const handleToggleConversationMenu = (e, chatId) => {
    e.stopPropagation();
    if (activeChatMenuId === chatId) {
      setActiveChatMenuId(null);
      setDesktopMenuPosition(null);
      return;
    }
    if (isPcMode) {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 176;
      const estimatedHeight = 150;
      const left = Math.max(
        8,
        Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      );
      const fitsBelow = rect.bottom + estimatedHeight + 8 <= window.innerHeight;
      setDesktopMenuPosition({
        left,
        top: fitsBelow ? rect.bottom + 4 : Math.max(8, rect.top - estimatedHeight - 4),
      });
    } else {
      setDesktopMenuPosition(null);
    }
    setActiveChatMenuId(chatId);
  };

  const handleTogglePin = async (e, chat) => {
    e.stopPropagation();
    setActiveChatMenuId(null);
    setDesktopMenuPosition(null);
    const nextPinned = !chat.pinned;
    const result = await setConversationPinned(chat.id, nextPinned);
    if (!result?.success) {
      showToast('대화 고정을 저장하지 못했어요. 다시 시도해주세요.', 'error');
      return;
    }
    showToast(nextPinned ? '대화를 목록 위에 고정했어요' : '대화 고정을 해제했어요', 'success');
  };

  const handleStartRename = (e, chat) => {
    e.stopPropagation();
    setActiveChatMenuId(null);
    setDesktopMenuPosition(null);
    setEditingChatId(chat.id);
    setEditingTitle(chat.title || '');
    setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 0);
  };

  const handleFinishRename = async () => {
    if (!editingChatId || renameSubmittingRef.current) return;
    if (!editingTitle.trim()) {
      showToast('대화 이름을 입력해주세요.', 'warning');
      setTimeout(() => renameInputRef.current?.focus(), 0);
      return;
    }
    renameSubmittingRef.current = true;
    const result = await renameConversation(editingChatId, editingTitle);
    if (result?.success) {
      setEditingChatId(null);
      setEditingTitle('');
      showToast('대화 이름을 바꿨어요', 'success');
    } else {
      showToast('대화 이름을 저장하지 못했어요. 다시 시도해주세요.', 'error');
      setTimeout(() => renameInputRef.current?.focus(), 0);
    }
    renameSubmittingRef.current = false;
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFinishRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingChatId(null);
      setEditingTitle('');
    }
  };

  const conversationItemHandlersRef = useRef(null);
  conversationItemHandlersRef.current = {
    handleChatSelect,
    handleFinishRename,
    handleRenameKeyDown,
    handleToggleConversationMenu,
  };
  const conversationItemHandlers = useMemo(() => ({
    onHoverChange: setHoveredChatId,
    onSelect: (chatId) => conversationItemHandlersRef.current.handleChatSelect(chatId),
    onEditingTitleChange: setEditingTitle,
    onFinishRename: () => conversationItemHandlersRef.current.handleFinishRename(),
    onRenameKeyDown: (e) => conversationItemHandlersRef.current.handleRenameKeyDown(e),
    onToggleMenu: (e, chatId) => conversationItemHandlersRef.current.handleToggleConversationMenu(e, chatId),
  }), []);

  // 검색 버튼 클릭 핸들러
  const handleSearchButtonClick = () => {
    setShowSearchInput(!showSearchInput);
    if (!showSearchInput) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery('');
    }
  };

  // 검색어 변경 핸들러
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // 검색 초기화 버튼 핸들러
  const handleClearSearch = () => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  };

  // 로그인 팝업 닫기
  const handleCloseLoginPrompt = () => {
    setShowLoginPrompt(false);
  };

  // 이용권 상태 뱃지 렌더링 (남은 일수에 따른 심플 강조)
  const renderSubscriptionBadge = () => {
    if (!subscriptionStatus) {
      return (
        <span
          style={{
            fontSize: '12px',
            fontWeight: '600',
            padding: '2px 8px',
            borderRadius: '12px',
            backgroundColor: 'var(--wq-surface-muted, #f3f4f6)',
            color: 'var(--wq-text-sub, #6b7280)',
            marginLeft: '6px',
            whiteSpace: 'nowrap',
          }}
        >
          확인 중
        </span>
      );
    }

    const isPro = subscriptionStatus.isPro || false;
    const isActive = subscriptionStatus.status === 'active';
    const isExpired = subscriptionStatus.status === 'expired';
    const remainingDays = getRemainingDays();
    const bubblePlanKey = proBubbleOffer?.recommended_plan || '3month';
    const bubblePricing = proBubbleOffer?.prices?.[bubblePlanKey];
    const bubblePlanLabel = bubblePlanKey.replace('month', '개월');

    let badgeText = '';
    let badgeColor = '';
    let textColor = '';
    let extraStyle = {};

    if ((subscriptionStatus.source === 'cache' || subscriptionStatus.verificationFailed) && isPro) {
      badgeText = 'PRO · 확인 중';
      badgeColor = 'var(--wq-accent-soft-strong, #dbeafe)';
      textColor = 'var(--wq-accent-text, #1d4ed8)';
    } else if (subscriptionStatus.verificationFailed || subscriptionStatus.type === 'unknown') {
      badgeText = '연결 확인 필요';
      badgeColor = 'var(--wq-warning-soft, #fff7ed)';
      textColor = 'var(--wq-warning-text, #9a3412)';
    } else if (isPro && isActive) {
      // PRO 활성 상태 - 항상 "PRO · N일" 형식
      if (remainingDays === null) {
        // 무제한 (쿠폰 등)
        badgeText = 'PRO';
        badgeColor = '#10b981';
        textColor = 'white';
      } else if (remainingDays > 7) {
        // 8일 이상: 안정 (초록)
        badgeText = `PRO · ${remainingDays}일`;
        badgeColor = '#10b981';
        textColor = 'white';
      } else if (remainingDays >= 1) {
        // 1~7일: 주의 (노란색)
        badgeText = `PRO · ${remainingDays}일`;
        badgeColor = 'var(--wq-warning-soft, #fef3c7)';
        textColor = 'var(--wq-warning-text, #92400e)';
        extraStyle = {
          boxShadow: '0 0 0 2px #fcd34d'
        };
      } else {
        // 0일: 오늘 마지막 (노란색 + 펄스)
        badgeText = `PRO · 0일`;
        badgeColor = 'var(--wq-warning-soft, #fef3c7)';
        textColor = 'var(--wq-warning-text, #92400e)';
        extraStyle = {
          boxShadow: '0 0 0 2px #f59e0b',
          animation: 'pulse-badge 2s ease-in-out infinite'
        };
      }
    } else if (isPro && isExpired) {
      badgeText = '만료됨';
      badgeColor = 'var(--wq-surface-strong, #e5e7eb)';
      textColor = 'var(--wq-text-sub, #6b7280)';
    } else {
      badgeText = 'FREE';
      badgeColor = 'var(--wq-surface-strong, #e5e7eb)';
      textColor = 'var(--wq-text-sub, #6b7280)';
    }

    return (
      <>
        <style>
          {`
            @keyframes pulse-badge {
              0%, 100% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.05); opacity: 0.9; }
            }
            @keyframes bubble-enter {
              0% { opacity: 0; transform: translateY(4px) scale(0.95); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}
        </style>
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <span
            style={{
              fontSize: '12px',
              fontWeight: '700',
              backgroundColor: badgeColor,
              color: textColor,
              padding: '2px 8px',
              borderRadius: '12px',
              marginLeft: '4px',
              lineHeight: '1',
              letterSpacing: '0.025em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              whiteSpace: 'nowrap',
              ...extraStyle
            }}
          >
            {badgeText}
          </span>
          
          {/* 만료 임박 말풍선 */}
          {showProBubble && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: isPcMode ? '-10px' : '0',
                backgroundColor: 'white',
                color: 'var(--wq-text, #374151)',
                padding: '12px 12px 10px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                zIndex: 1000,
                animation: 'bubble-enter 0.4s ease-out forwards',
                minWidth: '140px'
              }}
            >
              {/* 말풍선 꼬리 */}
              <div
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: isPcMode ? '20px' : '12px',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: '6px solid white',
                  filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.04))'
                }}
              />
              
              {/* 헤더 - 체험 사용자는 50%, 일반 PRO는 20% */}
              <div style={{ minHeight: '44px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ color: 'var(--wq-accent-text, #3182f6)', fontSize: '12px', fontWeight: '700' }}>
                  {proBubbleOffer?.title || (isTrialUser ? '첫 결제 50% 할인' : '지금 연장하면 20% 할인')}
                </div>
                <button
                  type="button"
                  aria-label="할인 안내 닫기"
                  onClick={handleDismissProBubble}
                  style={{
                    flexShrink: 0,
                    width: '44px',
                    height: '44px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '8px',
                    background: 'transparent',
                    color: 'var(--wq-text-faint, #9ca3af)',
                    fontSize: '16px',
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>

                {/* 메인 CTA */}
              <button
                onClick={handleProBubbleClick}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'var(--wq-accent-fill, #3182F6)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                <span>{bubblePlanLabel}</span>
                <span style={{ textDecoration: 'line-through', opacity: 0.7, fontSize: '12px' }}>
                  {(bubblePricing?.original_price || 60000).toLocaleString()}
                </span>
                <span>→ {(bubblePricing?.final_price || (isTrialUser ? 30000 : 48000)).toLocaleString()}원</span>
              </button>
            </div>
          )}
          
          {/* 새학기 이벤트 말풍선 - 데스크톱: 드롭다운, 모바일: 하단 스낵바 */}
          {showEventBubble && !showProBubble && isPcMode && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: '-10px',
                backgroundColor: 'white',
                color: 'var(--wq-text, #374151)',
                padding: '12px 12px 10px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                zIndex: 1000,
                animation: 'bubble-enter 0.4s ease-out forwards',
                minWidth: '160px'
              }}
            >
              {/* 말풍선 꼬리 */}
              <div
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '20px',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: '6px solid white',
                  filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.04))'
                }}
              />
              
              <div style={{ minHeight: '44px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--wq-accent-text, #3182F6)', fontSize: '12px', fontWeight: '700' }}>{seasonShortLabel}</span>
                <button
                  type="button"
                  aria-label="이벤트 안내 닫기"
                  onClick={handleDismissEventBubble}
                  style={{
                    flexShrink: 0,
                    width: '44px',
                    height: '44px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '8px',
                    background: 'transparent',
                    color: 'var(--wq-text-faint, #9ca3af)',
                    fontSize: '16px',
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>

              <button
                onClick={handleEventBubbleClick}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'var(--wq-accent-fill, #3182F6)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                {_maxDiscountCta}
              </button>
            </div>
          )}
        </span>
      </>
    );
  };

  // 대화 목록 렌더링 함수
  const renderChatList = () => {
    if (isAnonymous) {
      // 익명 사용자인 경우 로그인 유도 메시지 표시
      return (
        <div className="chat-list-login-prompt" style={{
          padding: '16px',
          backgroundColor: 'var(--wq-surface-subtle, #f8f9fa)',
          margin: '8px',
          borderRadius: '12px',
        }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px', color: 'var(--wq-text-strong, #191f28)' }}>
            로그인하고 대화 저장하기
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--wq-text-sub, #6b7684)', marginBottom: '12px', lineHeight: '1.5' }}>
            로그인하면 대화가 자동으로 저장돼요.
          </p>
          <button
            onClick={handleGoogleLogin}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: 'var(--wq-accent-fill, #3182f6)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <img 
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
              alt="Google"
              style={{ width: '18px', height: '18px', backgroundColor: 'white', borderRadius: '50%', padding: '2px' }}
            />
            로그인하기
          </button>
        </div>
      );
    }

    // 로그인한 사용자인 경우 정상적으로 대화 목록 표시
    const activeMenuChat = conversations.find((chat) => chat.id === activeChatMenuId);
    return (
      <div className="conversations-container" style={{ padding: '0 8px' }}>
        {showSearchInput && (
          <div className="search-container" style={{
            position: 'relative',
            margin: '8px 0',
          }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="대화 검색..."
              style={{
                width: '100%',
                padding: '8px 32px 8px 12px',
                borderRadius: '4px',
                border: '1px solid var(--wq-line, #e2e8f0)',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'var(--wq-text-faint, #94a3b8)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        )}
        
        {filteredChatHistory.length === 0 ? (
          <div style={{ 
            padding: '16px', 
            textAlign: 'center', 
            color: 'var(--wq-text-faint, #94a3b8)',
            fontSize: '14px'
          }}>
            {searchQuery ? '검색 결과가 없어요' : '아직 대화가 없어요'}
          </div>
        ) : (
          <div 
            className="conversation-list" 
            onScroll={() => {
              if (isPcMode && activeChatMenuId) {
                setActiveChatMenuId(null);
                setDesktopMenuPosition(null);
              }
            }}
            style={{ 
              marginTop: '8px',
              maxHeight: '320px', // 약 5개 항목이 보이는 높이
              overflowY: 'auto',
              // 스크롤바 스타일링
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 transparent',
            }}
          >
            {filteredChatHistory.map((chat) => (
              <ConversationListItem
                key={chat.id}
                chat={chat}
                isActive={currentConversation === chat.id}
                isHovered={hoveredChatId === chat.id}
                isEditing={editingChatId === chat.id}
                isMenuOpen={activeChatMenuId === chat.id}
                isPcMode={isPcMode}
                editingTitle={editingChatId === chat.id ? editingTitle : ''}
                renameInputRef={renameInputRef}
                {...conversationItemHandlers}
              />
            ))}
          </div>
        )}
        {isPcMode && activeMenuChat && desktopMenuPosition && typeof document !== 'undefined' && createPortal(
          <div
            data-chat-menu
            role="menu"
            aria-label={`${activeMenuChat.title} 대화 메뉴`}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: `${desktopMenuPosition.left}px`,
              top: `${desktopMenuPosition.top}px`,
              zIndex: 3000,
              width: '176px',
              padding: '6px',
              background: 'var(--wq-surface, #ffffff)',
              borderRadius: '12px',
              boxShadow: '0 10px 32px rgba(15, 23, 42, 0.18)',
            }}
          >
            <button
              role="menuitem"
              onClick={(e) => handleStartRename(e, activeMenuChat)}
              style={conversationMenuItemStyle}
            >
              이름 바꾸기
            </button>
            <button
              role="menuitem"
              onClick={(e) => handleTogglePin(e, activeMenuChat)}
              style={conversationMenuItemStyle}
            >
              {activeMenuChat.pinned ? '고정 해제' : '상단에 고정'}
            </button>
            <button
              role="menuitem"
              onClick={(e) => handleDeleteConversation(e, activeMenuChat.id)}
              style={{ ...conversationMenuItemStyle, color: 'var(--wq-danger-text, #e5484d)' }}
            >
              대화 삭제
            </button>
          </div>,
          document.body,
        )}
        {/* 모바일 대화 메뉴 — 핸들(끌어서 닫기)·스크림·ESC·닫기 버튼은 Sheet 프리미티브가 맡는다 */}
        {!isPcMode && activeMenuChat && (
          <Sheet
            closeLabel="대화 메뉴 닫기"
            dragHandle
            onClose={() => setActiveChatMenuId(null)}
            open
            testId="conversation-menu-sheet"
            title={<span style={conversationSheetTitleStyle}>{activeMenuChat.title}</span>}
          >
            <div
              data-chat-menu
              role="menu"
              aria-label={`${activeMenuChat.title} 대화 메뉴`}
              style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
            >
              <button
                role="menuitem"
                onClick={(e) => handleStartRename(e, activeMenuChat)}
                style={conversationSheetItemStyle}
              >
                이름 바꾸기
              </button>
              <button
                role="menuitem"
                onClick={(e) => handleTogglePin(e, activeMenuChat)}
                style={conversationSheetItemStyle}
              >
                {activeMenuChat.pinned ? '고정 해제' : '상단에 고정'}
              </button>
              <button
                role="menuitem"
                onClick={(e) => handleDeleteConversation(e, activeMenuChat.id)}
                style={{ ...conversationSheetItemStyle, color: 'var(--wq-danger-text, #f04452)' }}
              >
                대화 삭제
              </button>
            </div>
          </Sheet>
        )}
      </div>
    );
  };

  // 사이드 메뉴 콘텐츠
  const renderSideMenu = () => (
    <div
      ref={mobileMenuRef}
      className="app-side-nav"
      aria-hidden={!isPcMode && !isMenuOpen}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isPcMode ? 'var(--whatquiz-nav-width, 240px)' : '80%',
        maxWidth: isPcMode ? 'var(--whatquiz-nav-width, 240px)' : '300px',
        backgroundColor: 'var(--wq-surface, #ffffff)',
        zIndex: 'var(--whatquiz-z-nav, 1000)',
        overflowY: 'auto',
        transition: 'transform 0.3s ease, width 0.3s ease, max-width 0.3s ease',
        transform: isMenuOpen || isPcMode ? 'translateX(0)' : 'translateX(-100%)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isPcMode ? '0 0 20px rgba(0, 0, 0, 0.08)' : '0 0 20px rgba(0, 0, 0, 0.15)',
      }}
    >
      {/* 메뉴 헤더 (모바일에서만 표시) */}
      {!isPcMode && (
        <div style={{
          padding: '20px',
          borderBottom: '1px solid var(--wq-line, #f0f4f8)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
          }}>
            {currentUser && currentUser.photoURL ? (
              <img 
                src={currentUser.photoURL} 
                alt="프로필" 
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : currentUser ? (
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: 'var(--wq-accent-fill, #3182F6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
              }}>
                {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
              </div>
            ) : (
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: 'var(--wq-accent-fill, #3182F6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            )}
            
            <div>
              {currentUser ? (
                <>
                  <div style={{ 
                    fontWeight: 'bold', 
                    color: 'var(--wq-text, #333)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    {currentUser.displayName || '사용자'}
                    {renderSubscriptionBadge()}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--wq-text-sub, #777)' }}>
                    {currentUser.email}
                  </div>
                </>
              ) : (
                <div style={{
                  color: 'var(--wq-accent-text, #3182F6)',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <img src="/images/whatquiz-character.png" alt="" style={{ width: 28, height: 28, borderRadius: 8 }} />
                  왓퀴즈
                </div>
              )}
            </div>
          </div>
          
          <button
            type="button"
            aria-label="사이드 메뉴 닫기"
            onClick={() => setIsMenuOpen(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--wq-text-sub, #777)',
              cursor: 'pointer',
              width: '44px',
              height: '44px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}
      
      {/* PC 모드에서 상단 로고 표시 */}
      {isPcMode && (
        <div style={{
          padding: '24px 20px',
          textAlign: 'center',
          borderBottom: '1px solid var(--wq-line, #f0f4f8)',
        }}>
          <Link to="/home" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: 'var(--wq-accent-text, #3182F6)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <img src="/images/whatquiz-character.png" alt="" style={{ width: 28, height: 28, borderRadius: 8 }} />
            왓퀴즈
          </Link>
        </div>
      )}
      
      {/* 메뉴 항목들 */}
      <div style={{ padding: '16px 0' }}>
        {/* AI 채팅 메뉴 */}
        <div>
          <button 
            onClick={() => {
              !isPcMode && setIsMenuOpen(false);
              // 전문가 선택창으로 이동
              navigate('/chat?new=true');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 20px',
              color: isChatSection ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
              background: 'none',
              border: 'none',
              fontWeight: isChatSection ? '600' : '500',
              backgroundColor: isChatSection ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
              borderRadius: '12px',
              margin: '2px 8px',
              width: 'calc(100% - 16px)',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              <circle cx="9" cy="10" r="1" fill="#3182F6" stroke="none"></circle>
              <circle cx="12" cy="10" r="1" fill="#3182F6" stroke="none"></circle>
              <circle cx="15" cy="10" r="1" fill="#3182F6" stroke="none"></circle>
            </svg>
            <span>AI 채팅</span>
          </button>

          {/* AI 맞춤 설정 - 채팅 섹션(로그인 사용자)에서만 표시. 생기부 '내 스타일' 하위 항목과 같은 패턴 */}
          {isChatSection && currentUser && !isAnonymous && (
            <Link
              className={`nav-hover-sub${isChatSettingsPage ? ' is-active' : ''}`}
              to="/chat/settings"
              onClick={() => !isPcMode && setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 20px 12px 52px',
                color: isChatSettingsPage ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: isChatSettingsPage ? 'var(--wq-accent-soft, #e8f0fe)' : 'transparent',
                width: '100%',
                textAlign: 'left',
                borderRadius: '8px',
                margin: '2px 8px',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isChatSettingsPage ? '#3182F6' : '#6b7684'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3.5a5.5 5.5 0 0 0-5.45 4.74A4.5 4.5 0 0 0 7.5 17H9v2.5a1 1 0 0 0 1.62.78L14.7 17H16a4.5 4.5 0 0 0 1.45-8.76A5.5 5.5 0 0 0 12 3.5Z"></path>
                </svg>
                <span style={{ color: 'inherit' }}>AI 맞춤 설정</span>
              </div>
            </Link>
          )}
          
          {/* AI 상담 하위 메뉴를 채팅 페이지일 때만 표시 */}
          {(isChatPage || (location.pathname === '/home' && !isPcMode && isMenuOpen)) && (
            <div 
              style={{
                backgroundColor: 'var(--wq-surface-subtle, #f8fafc)',
              }}
            >
              {/* 대화 목록 헤더 */}
              <div className="chat-history-header" style={{
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--wq-text-sub, #64748b)' }}>
                  {isAnonymous ? '대화 목록' : '내 대화 목록'}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* 검색 버튼 - 익명 사용자가 아닐 때만 표시 */}
                  {!isAnonymous && (
                    <button
                      className="search-button"
                      onClick={handleSearchButtonClick}
                      aria-label="대화 검색"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: showSearchInput ? 'var(--wq-accent-text, #4a6cf7)' : 'var(--wq-text-faint, #94a3b8)',
                        borderRadius: '4px',
                        padding: '4px',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </button>
                  )}

                  {/* 새 채팅 버튼 - 제미나이 스타일 */}
                  <button
                    className="nav-hover-chip"
                    onClick={() => {
                      // 모바일에서 네비게이션 닫기
                      if (!isPcMode) setIsMenuOpen(false);
                      // 전문가 선택창으로 이동 (기존 대화 초기화)
                      navigate('/chat?new=true');
                    }}
                    aria-label="새 채팅"
                    style={{
                      background: 'none',
                      border: '1px solid var(--wq-line, #e2e8f0)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: 'var(--wq-text-sub, #64748b)',
                      borderRadius: '16px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    새 채팅
                  </button>
                </div>
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                {renderChatList()}
              </div>
            </div>
          )}
        </div>
        
        {/* ── 구분선: 대화 | 생기부 ── */}
        <div style={{ height: '1px', background: 'var(--wq-surface-muted, #f2f4f6)', margin: '8px 16px' }} />
        
        {/* 빠른 생기부 생성 메뉴 */}
        <div>
          <Link
            to="/create-saenggibu"
            onClick={() => !isPcMode && setIsMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 20px',
              color: (
                location.pathname.startsWith('/create-saenggibu') || 
                location.pathname.startsWith('/saenggibu-result') ||
                location.pathname.startsWith('/saenggibu-library') ||
                location.pathname.startsWith('/saenggibu-history')
              ) ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
              textDecoration: 'none',
              fontWeight: (
                location.pathname.startsWith('/create-saenggibu') || 
                location.pathname.startsWith('/saenggibu-result') ||
                location.pathname.startsWith('/saenggibu-library') ||
                location.pathname.startsWith('/saenggibu-history') ||
                location.pathname.startsWith('/saenggibu-my-style')
              ) ? '600' : '500',
              backgroundColor: (
                location.pathname.startsWith('/create-saenggibu') || 
                location.pathname.startsWith('/saenggibu-result') ||
                location.pathname.startsWith('/saenggibu-library') ||
                location.pathname.startsWith('/saenggibu-history') ||
                location.pathname.startsWith('/saenggibu-my-style')
              ) ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
              borderRadius: '12px',
              margin: '2px 8px',
              transition: 'all 0.15s ease',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <path d="M18 2l3 3-7 7H11v-3l7-7z"></path>
            </svg>
            <span>AI 생기부 생성</span>
          </Link>
          
          {/* 엑셀로 일괄 처리 - create-saenggibu에 통합되어 제거 */}
          
          {/* 내 생기부 기록 - 생기부 관련 페이지에서만 표시 */}
          {(
            location.pathname.startsWith('/create-saenggibu') || 
            location.pathname.startsWith('/saenggibu-result') ||
            location.pathname.startsWith('/saenggibu-library') ||
            location.pathname.startsWith('/saenggibu-history') ||
            location.pathname.startsWith('/saenggibu-my-style')
          ) && (
            <Link
              className={`nav-hover-sub${location.pathname.startsWith('/saenggibu-history') ? ' is-active' : ''}`}
              to="/saenggibu-history"
              onClick={() => !isPcMode && setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 20px 12px 52px',
                color: location.pathname.startsWith('/saenggibu-history') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: location.pathname.startsWith('/saenggibu-history') ? 'var(--wq-accent-soft, #e8f0fe)' : 'transparent',
                width: '100%',
                textAlign: 'left',
                borderRadius: '8px',
                margin: '2px 8px',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={location.pathname.startsWith('/saenggibu-history') ? '#3182F6' : '#6b7684'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  <path d="M8 14h8"></path>
                  <path d="M8 18h5"></path>
                </svg>
                <span style={{ color: 'inherit' }}>내 생기부 기록</span>
              </div>
            </Link>
          )}

          {/* 내 스타일 - 생기부 관련 페이지에서만 표시 */}
          {(
            location.pathname.startsWith('/create-saenggibu') || 
            location.pathname.startsWith('/saenggibu-result') ||
            location.pathname.startsWith('/saenggibu-library') ||
            location.pathname.startsWith('/saenggibu-history') ||
            location.pathname.startsWith('/saenggibu-my-style')
          ) && (
            <Link
              className={`nav-hover-sub${location.pathname.startsWith('/saenggibu-my-style') ? ' is-active' : ''}`}
              to="/saenggibu-my-style"
              onClick={() => !isPcMode && setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 20px 12px 52px',
                color: location.pathname.startsWith('/saenggibu-my-style') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: location.pathname.startsWith('/saenggibu-my-style') ? 'var(--wq-accent-soft, #e8f0fe)' : 'transparent',
                width: '100%',
                textAlign: 'left',
                borderRadius: '8px',
                margin: '2px 8px',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={location.pathname.startsWith('/saenggibu-my-style') ? '#3182F6' : '#6b7684'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                </svg>
                <span style={{ color: 'inherit' }}>내 스타일</span>
              </div>
            </Link>
          )}
        </div>
        
        {/* AI 생기부 검사기 */}
        <div>
          <Link
            to="/saenggibu-inspector"
            onClick={() => !isPcMode && setIsMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 20px',
              color: (
                location.pathname.startsWith('/saenggibu-inspector') || 
                location.pathname.startsWith('/inspector-history')
              ) ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
              textDecoration: 'none',
              fontWeight: (
                location.pathname.startsWith('/saenggibu-inspector') || 
                location.pathname.startsWith('/inspector-history')
              ) ? '600' : '500',
              backgroundColor: (
                location.pathname.startsWith('/saenggibu-inspector') || 
                location.pathname.startsWith('/inspector-history')
              ) ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
              borderRadius: '12px',
              margin: '2px 8px',
              transition: 'all 0.15s ease',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <path d="M9 15l2 2 4-4"></path>
            </svg>
            <span>AI 생기부 검사기</span>
          </Link>
          
          {/* 검사 기록 - 검사기 관련 페이지에서만 표시 */}
          {(
            location.pathname.startsWith('/saenggibu-inspector') || 
            location.pathname.startsWith('/inspector-history')
          ) && (
            <Link
              className={`nav-hover-sub${location.pathname.startsWith('/inspector-history') ? ' is-active' : ''}`}
              to="/inspector-history"
              onClick={() => !isPcMode && setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 20px 12px 52px',
                color: location.pathname.startsWith('/inspector-history') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: location.pathname.startsWith('/inspector-history') ? 'var(--wq-accent-soft, #e8f0fe)' : 'transparent',
                width: '100%',
                textAlign: 'left',
                borderRadius: '8px',
                margin: '2px 8px',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={location.pathname.startsWith('/inspector-history') ? '#3182F6' : '#6b7684'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  <path d="M8 14h8"></path>
                  <path d="M8 18h5"></path>
                </svg>
                <span style={{ color: 'inherit' }}>내 생기부 검사 기록</span>
              </div>
            </Link>
          )}
        </div>
        
        {/* ── 구분선: 생기부 | 평가 ── */}
        <div style={{ height: '1px', background: 'var(--wq-surface-muted, #f2f4f6)', margin: '8px 16px' }} />
        
        {/* AI 서술형 채점 */}
        <div>
          <Link
            to="/scoring"
            onClick={() => !isPcMode && setIsMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 20px',
              color: (
                location.pathname === '/scoring' || 
                location.pathname.startsWith('/scoring-history')
              ) ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
              textDecoration: 'none',
              fontWeight: (
                location.pathname === '/scoring' || 
                location.pathname.startsWith('/scoring-history')
              ) ? '600' : '500',
              backgroundColor: (
                location.pathname === '/scoring' || 
                location.pathname.startsWith('/scoring-history')
              ) ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
              borderRadius: '12px',
              margin: '2px 8px',
              transition: 'all 0.15s ease',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
              <path d="M9 14l2 2 4-4"></path>
            </svg>
            <span>AI 서술형 채점</span>
          </Link>
          
          {/* 채점 기록 - 채점 관련 페이지에서만 표시 */}
          {(
            location.pathname === '/scoring' || 
            location.pathname.startsWith('/scoring-history')
          ) && (
            <Link
              className={`nav-hover-sub${location.pathname.startsWith('/scoring-history') ? ' is-active' : ''}`}
              to="/scoring-history"
              onClick={() => !isPcMode && setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 20px 12px 52px',
                color: location.pathname.startsWith('/scoring-history') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: location.pathname.startsWith('/scoring-history') ? 'var(--wq-accent-soft, #e8f0fe)' : 'transparent',
                width: '100%',
                textAlign: 'left',
                borderRadius: '8px',
                margin: '2px 8px',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={location.pathname.startsWith('/scoring-history') ? '#3182F6' : '#6b7684'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  <path d="M8 14h8"></path>
                  <path d="M8 18h5"></path>
                </svg>
                <span style={{ color: 'inherit' }}>채점 기록</span>
              </div>
            </Link>
          )}
        </div>
        
        {/* 연간 평가계획서 */}
        <Link
          to="/assessment-plan"
          onClick={() => !isPcMode && setIsMenuOpen(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            color: location.pathname.startsWith('/assessment-plan') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
            textDecoration: 'none',
            fontWeight: location.pathname.startsWith('/assessment-plan') ? '600' : '500',
            backgroundColor: location.pathname.startsWith('/assessment-plan') ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
            borderRadius: '12px',
            margin: '2px 8px',
            transition: 'all 0.15s ease',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
            <path d="M8 14h.01"></path>
            <path d="M12 14h.01"></path>
            <path d="M16 14h.01"></path>
            <path d="M8 18h.01"></path>
            <path d="M12 18h.01"></path>
          </svg>
          <span>AI 평가계획서</span>
        </Link>

          {/* 평가계획서 기록 - 평가계획서 관련 페이지에서만 표시 */}
          {(
            location.pathname === '/assessment-plan' || 
            location.pathname.startsWith('/assessment-plan-history')
          ) && (
            <Link
              className={`nav-hover-sub${location.pathname.startsWith('/assessment-plan-history') ? ' is-active' : ''}`}
              to="/assessment-plan-history"
              onClick={() => !isPcMode && setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 20px 12px 52px',
                color: location.pathname.startsWith('/assessment-plan-history') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: location.pathname.startsWith('/assessment-plan-history') ? 'var(--wq-accent-soft, #e8f0fe)' : 'transparent',
                width: '100%',
                textAlign: 'left',
                borderRadius: '8px',
                margin: '2px 8px',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={location.pathname.startsWith('/assessment-plan-history') ? '#3182F6' : '#6b7684'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  <path d="M8 14h8"></path>
                  <path d="M8 18h5"></path>
                </svg>
                <span style={{ color: 'inherit' }}>계획서 기록</span>
              </div>
            </Link>
          )}
        
        {/* ── 구분선: 평가 | 라이브러리·요금제 ── */}
        <div style={{ height: '1px', background: 'var(--wq-surface-muted, #f2f4f6)', margin: '8px 16px' }} />

        {/* 라이브러리 */}
        <Link
          className={`nav-hover-row${location.pathname.startsWith('/library') ? ' is-active' : ''}`}
          to="/library"
          onClick={() => !isPcMode && setIsMenuOpen(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            color: location.pathname.startsWith('/library') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
            textDecoration: 'none',
            fontWeight: location.pathname.startsWith('/library') ? '600' : '500',
            backgroundColor: location.pathname.startsWith('/library') ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
            borderRadius: '12px',
            margin: '2px 8px',
            transition: 'all 0.15s ease',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            <path d="M8 7h8"></path>
            <path d="M8 11h6"></path>
          </svg>
          <span>라이브러리</span>
        </Link>

        
        {/* 요금제 메뉴 — 단체 50% 이벤트 기간엔 필을 붙이고 학교·단체 밴드(#docs)로 바로 내려준다 */}
        <Link
          to={isGroup50Active ? '/pricing#docs' : '/pricing'}
          data-testid="nav-pricing-link"
          onClick={() => {
            if (!isPcMode) setIsMenuOpen(false);
            if (isPro50EventOn) trackPro50Event('crosslink_clicked', { stage: 'sidebar_pricing' });
            else if (isGroup50Active) trackGroup50Event('crosslink_clicked', { stage: 'sidebar_pricing' });
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            color: location.pathname.startsWith('/pricing') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
            textDecoration: 'none',
            fontWeight: location.pathname.startsWith('/pricing') ? '600' : '500',
            backgroundColor: location.pathname.startsWith('/pricing') ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
            borderRadius: '12px',
            margin: '2px 8px',
            transition: 'all 0.15s ease',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 3h12l4 6-10 13L2 9l4-6z"></path>
            <path d="M2 9h20"></path>
          </svg>
          <span>요금제</span>
          {(isPro50EventOn || isGroup50Active) && (
            <span
              data-testid={isPro50EventOn ? 'nav-pro50-pill' : 'nav-group50-pill'}
              style={{
                marginLeft: 'auto',
                padding: '2px 8px',
                borderRadius: '999px',
                backgroundColor: 'var(--wq-accent-soft, #e8f3ff)',
                color: 'var(--wq-accent-text, #3182F6)',
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {isPro50EventOn ? '10월 50%' : '단체 50%'}
            </span>
          )}
        </Link>
        


        


        
        {/* 관리자 메뉴 */}
        {currentUser && userDetails?.role === 'admin' && (
          <Link 
            to="/admin" 
            onClick={() => !isPcMode && setIsMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 20px',
              color: location.pathname.startsWith('/admin') ? 'var(--wq-accent-text, #3182F6)' : 'var(--wq-text, #333)',
              textDecoration: 'none',
              fontWeight: location.pathname.startsWith('/admin') ? '600' : '500',
              backgroundColor: location.pathname.startsWith('/admin') ? 'var(--wq-accent-soft, #f0f4ff)' : 'transparent',
              borderRadius: '12px',
              margin: '2px 8px',
              transition: 'all 0.15s ease',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1l3 6 6 .75-4.12 4.62L18 19l-6-3-6 3 1.88-6.63L3 7.75 9 7z"></path>
            </svg>
            <span>관리자 홈</span>
          </Link>
        )}
        

      </div>
      
      {/* 하단 고정 영역 */}
      <div style={{ marginTop: 'auto' }}>
        {/* 버전 정보 */}
        <div style={{
          fontSize: '12px',
          color: 'var(--wq-text-faint, #94a3b8)',
          textAlign: 'center',
          padding: '12px 0',
        }}>
          © 2026 왓퀴즈
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .nav-hover-bg:hover {
          background-color: var(--wq-surface-subtle, #f8fafc) !important;
        }
        .nav-hover-bg--gray:hover {
          background-color: var(--wq-surface-muted, #f2f4f6) !important;
        }
        .nav-hover-row:not(.is-active):hover {
          background-color: var(--wq-surface-subtle, #f8fafc) !important;
        }
        .nav-hover-sub:not(.is-active):hover {
          background-color: var(--wq-surface-subtle, #f8fafc) !important;
        }
        .nav-hover-sub:hover {
          transform: translateX(2px);
        }
        .nav-hover-chip:hover {
          background-color: var(--wq-surface-muted, #f1f5f9) !important;
          color: var(--wq-text, #334155) !important;
        }
        .nav-hover-primary:hover {
          background: #1b64da !important;
        }
        .nav-user-name {
          white-space: nowrap;
        }
        @media (max-width: 640px) {
          .nav-user-name {
            max-width: 80px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      `}</style>
      {/* 상단 네비게이션 바 */}
      <nav className="app-top-nav" style={{
        backgroundColor: 'var(--wq-surface, #ffffff)',
        color: 'var(--wq-text, #333)',
        padding: '10px 16px',
        position: 'sticky',
        top: 0,
        zIndex: 'var(--whatquiz-z-nav, 1000)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 1px 0 rgba(0, 0, 0, 0.06)',
        marginLeft: isPcMode ? 'var(--whatquiz-nav-width, 240px)' : '0',
        transition: 'margin-left 0.3s ease, width 0.3s ease',
        width: isPcMode ? 'calc(100% - var(--whatquiz-nav-width, 240px))' : '100%',
      }}>
        {/* 왼쪽: 햄버거 메뉴와 로고 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
          {!isPcMode && (
            <button 
              type="button"
              ref={mobileButtonRef}
              data-mobile-menu
              aria-label={isMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
              aria-expanded={isMenuOpen}
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
              }}
              style={{
                position: 'relative',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                width: '44px',
                height: '44px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--wq-text-sub, #777)',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
              {/* 단체 50% 이벤트 새 소식 도트 — 랜딩을 한 번 보면 사라진다 (PC는 사이드바 필이 항상 보여 생략) */}
              {showGroup50Dot && (
                <span
                  data-testid="nav-group50-hamburger-dot"
                  aria-hidden="true"
                  style={{ ...group50DotStyle, position: 'absolute', top: '8px', right: '8px' }}
                />
              )}
            </button>
          )}
          
          {!isPcMode && (
            <Link to="/home" style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: 'var(--wq-accent-text, #3182F6)',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}>
              <img src="/images/whatquiz-character.png" alt="" style={{ width: 24, height: 24, borderRadius: 6 }} />
              왓퀴즈
            </Link>
          )}

          {/* PC 모드 보조 로고 — 사이드바가 collapse(공문서 워크스페이스 모드)됐을 때만 노출.
              평상시엔 숨김(사이드바에 이미 로고가 있음). CSS .app-top-logo-pc 로 토글. */}
          {isPcMode && (
            <Link to="/home" className="app-top-logo-pc" style={{
              fontSize: '1.15rem',
              fontWeight: 'bold',
              color: 'var(--wq-accent-text, #3182F6)',
              textDecoration: 'none',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}>
              <img src="/images/whatquiz-character.png" alt="" style={{ width: 24, height: 24, borderRadius: 6 }} />
              왓퀴즈
            </Link>
          )}
        </div>
        
        {/* 오른쪽: 사용자 메뉴 */}
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {/* 사용자 메뉴 */}
          {currentUser ? (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button
                ref={userButtonRef}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'var(--wq-surface-muted, #f0f4f8)',
                  border: 'none',
                  borderRadius: '50px',
                  padding: '6px 12px',
                  color: 'var(--wq-text, #333)',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                {currentUser.photoURL ? (
                  <img 
                    src={currentUser.photoURL} 
                    alt="프로필" 
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                )}
                <span className="nav-user-name">{currentUser.displayName || '사용자'}</span>
                {hasUnseenWhatsNew && (
                  <span
                    data-testid="nav-whats-new-profile-dot"
                    aria-hidden="true"
                    style={{ ...group50DotStyle, position: 'absolute', top: '4px', right: '4px' }}
                  />
                )}
              </button>
              {renderSubscriptionBadge()}

              {/* 사용자 드롭다운 메뉴 */}
              {isUserMenuOpen && (
                <div 
                  ref={userMenuRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    backgroundColor: 'var(--wq-surface, #ffffff)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                    borderRadius: '12px',
                    padding: '8px 0',
                    minWidth: '180px',
                    marginTop: '8px',
                    zIndex: 1001,
                  }}
                >
                  {userDetails?.role === 'admin' && (
                    <Link 
                      to="/admin" 
                      onClick={() => setIsUserMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 16px',
                        color: 'var(--wq-text, #333)',
                        textDecoration: 'none',
                        fontSize: '14px',
                        minHeight: '44px',
                        borderBottom: '1px solid var(--wq-line, #f0f4f8)',
                        fontWeight: '500',
                      }}
                    >
                      관리자 홈
                    </Link>
                  )}

                  {isTrainingPartner && (
                    <Link
                      to="/partner/dashboard"
                      onClick={() => setIsUserMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 16px',
                        color: 'var(--wq-accent-text, #1e40af)',
                        backgroundColor: 'var(--wq-accent-soft, #eef6ff)',
                        textDecoration: 'none',
                        fontSize: '14px',
                        minHeight: '44px',
                        borderBottom: '1px solid var(--wq-accent-line, #dbeafe)',
                        fontWeight: '700',
                      }}
                    >
                      강사 센터
                    </Link>
                  )}
                  
                  <Link 
                    to="/subscription" 
                    onClick={() => setIsUserMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 16px',
                      color: 'var(--wq-text, #333)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      minHeight: '44px',
                      borderBottom: '1px solid var(--wq-line, #f0f4f8)',
                      fontWeight: '500',
                    }}
                  >
                    내 계정
                  </Link>

                  {isSchoolAdmin && (
                    <Link
                      className="nav-hover-bg"
                      to="/school-admin"
                      data-testid="nav-profile-school-admin"
                      onClick={() => setIsUserMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        color: 'var(--wq-text, #333)',
                        textDecoration: 'none',
                        fontSize: '14px',
                        minHeight: '44px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      학교 관리
                    </Link>
                  )}

                  {schoolReviewTasks > 0 && (
                    <Link
                      className="nav-hover-bg"
                      to="/school-review"
                      data-testid="nav-profile-school-review"
                      onClick={() => setIsUserMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        color: 'var(--wq-text, #333)',
                        textDecoration: 'none',
                        fontSize: '14px',
                        minHeight: '44px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      내 생기부 검토
                      <span
                        style={{
                          marginLeft: 6,
                          minWidth: 18,
                          height: 18,
                          padding: '0 5px',
                          borderRadius: 999,
                          background: '#3182f6',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {schoolReviewTasks}
                      </span>
                    </Link>
                  )}

                  {/* 학급 관리 — 저장한 반 명단을 보고 고치는 곳 (채점·생기부 이름 맞추기에 쓰인다) */}
                  <Link
                    className="nav-hover-bg"
                    to="/classes"
                    data-testid="nav-profile-classes"
                    onClick={() => setIsUserMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      color: 'var(--wq-text, #333)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      minHeight: '44px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    학급 관리
                  </Link>

                  {/* 연수원 — 온라인 연수 목록. 수강 중인 연수는 연수원 상단 "내 연수"에서 이어 본다. */}
                  <Link
                    className="nav-hover-bg"
                    to="/training"
                    data-testid="nav-profile-training"
                    onClick={() => setIsUserMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      color: 'var(--wq-text, #333)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      minHeight: '44px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    연수원
                  </Link>

                  {/* 왓퀴즈 이벤트 — 허브(/events)로 이동. 단체 50%는 허브 카드에서 고른다. */}
                  <Link 
                    className="nav-hover-bg"
                    to="/events"
                    data-testid="nav-profile-events-link"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      color: 'var(--wq-text, #333)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      minHeight: '44px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    이벤트
                    {showGroup50Dot && (
                      <span data-testid="nav-group50-profile-dot" aria-hidden="true" style={group50DotStyle} />
                    )}
                  </Link>

                  {/* 새 소식 — 최근 업데이트 카드뉴스를 다시 연다 (없으면 고객센터 업데이트 목록) */}
                  <button
                    className="nav-hover-bg"
                    type="button"
                    data-testid="nav-profile-whats-new"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      openWhatsNew();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '8px 16px',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--wq-text, #333)',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      minHeight: '44px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    새 소식
                    {hasUnseenWhatsNew && (
                      <span data-testid="nav-whats-new-menu-dot" aria-hidden="true" style={group50DotStyle} />
                    )}
                  </button>

                  {/* 고객센터 */}
                  <Link 
                    className="nav-hover-bg"
                    to="/help" 
                    onClick={() => setIsUserMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      color: 'var(--wq-text, #333)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      minHeight: '44px',
                      fontWeight: '500',
                      borderBottom: '1px solid var(--wq-line, #f0f4f8)',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    고객센터
                  </Link>

                  <button 
                    onClick={handleLogout}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--wq-danger-text, #f44336)',
                      fontSize: '14px',
                      minHeight: '44px',
                      fontWeight: '500',
                    }}
                  >
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <button
                className="nav-hover-primary"
                onClick={handleGoogleLogin}
                style={{
                  background: 'var(--wq-accent-fill, #3182F6)',
                  color: 'white',
                  textDecoration: 'none',
                  fontWeight: '500',
                  padding: '6px 16px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                }}
              >
                <img 
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
                  alt="G" 
                  style={{ width: '14px', height: '14px', backgroundColor: 'white', borderRadius: '50%', padding: '1px' }}
                />
                왓퀴즈 로그인
              </button>
              
              {/* 비로그인 사용자용 새학기 이벤트 말풍선 - 데스크톱만 */}
              {showEventBubble && !showProBubble && isPcMode && (
                <>
                  <style>
                    {`
                      @keyframes bubble-enter {
                        0% { opacity: 0; transform: translateY(4px) scale(0.95); }
                        100% { opacity: 1; transform: translateY(0) scale(1); }
                      }
                    `}
                  </style>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: '-10px',
                    backgroundColor: 'white',
                    color: 'var(--wq-text, #374151)',
                    padding: '12px 12px 10px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                    zIndex: 1000,
                    animation: 'bubble-enter 0.4s ease-out forwards',
                    minWidth: '160px'
                  }}
                >
                  {/* 말풍선 꼬리 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '20px',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderBottom: '6px solid white',
                      filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.04))'
                    }}
                  />
                  
                  <div style={{ minHeight: '44px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span style={{ color: 'var(--wq-accent-text, #3182F6)', fontSize: '12px', fontWeight: '700' }}>{seasonShortLabel}</span>
                    <button
                      type="button"
                      aria-label="이벤트 안내 닫기"
                      onClick={handleDismissEventBubble}
                      style={{
                        flexShrink: 0,
                        width: '44px',
                        height: '44px',
                        padding: 0,
                        border: 'none',
                        borderRadius: '8px',
                        background: 'transparent',
                        color: 'var(--wq-text-faint, #9ca3af)',
                        fontSize: '16px',
                        lineHeight: 1,
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <button
                    onClick={handleEventBubbleClick}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: 'var(--wq-accent-fill, #3182F6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    {_maxDiscountCta}
                  </button>
                </div>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* 모바일 전용: 어태치드 바텀시트 이벤트 프로모션 (pull-to-dismiss 지원) */}
      {!isPcMode && showEventBubble && !showProBubble && (
        <>
          <style>
            {`
              @keyframes bottomsheet-slide-up {
                0% { opacity: 0; transform: translateY(100%); }
                100% { opacity: 1; transform: translateY(0); }
              }
            `}
          </style>
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderRadius: '16px 16px 0 0',
              padding: '12px 20px 20px',
              paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.10)',
              zIndex: 1000,
              animation: 'bottomsheet-slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards',
              willChange: 'transform',
            }}
            onTouchStart={(e) => {
              const el = e.currentTarget;
              el.dataset.touchStartY = e.touches[0].clientY;
              el.dataset.touchDelta = '0';
            }}
            onTouchMove={(e) => {
              const el = e.currentTarget;
              const startY = parseFloat(el.dataset.touchStartY);
              const deltaY = e.touches[0].clientY - startY;
              el.dataset.touchDelta = String(deltaY);
              if (deltaY > 0) {
                el.style.transform = `translateY(${deltaY}px)`;
                el.style.opacity = String(Math.max(0.3, 1 - deltaY / 300));
                el.style.transition = 'none';
                el.style.animation = 'none';
              }
            }}
            onTouchEnd={(e) => {
              const el = e.currentTarget;
              const deltaY = parseFloat(el.dataset.touchDelta || '0');
              if (deltaY > 80) {
                el.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
                el.style.transform = 'translateY(100%)';
                el.style.opacity = '0';
                setTimeout(() => {
                  handleDismissEventBubble(e);
                }, 300);
              } else {
                el.style.transition = 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease';
                el.style.transform = 'translateY(0)';
                el.style.opacity = '1';
              }
            }}
          >
            {/* 핸들바 - 드래그 가능 시각 표시 */}
            <div style={{
              width: '36px',
              height: '4px',
              backgroundColor: 'var(--wq-surface-strong, #D1D5DB)',
              borderRadius: '2px',
              margin: '0 auto 16px',
              cursor: 'grab',
            }} />

            {/* 닫기 버튼 */}
            <button
              onClick={handleDismissEventBubble}
              style={{
                position: 'absolute',
                top: '12px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: 'var(--wq-text-faint, #9ca3af)',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '6px',
                lineHeight: 1,
                minWidth: '32px',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
              }}
            >
              ×
            </button>

            {/* 텍스트 영역 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '17px',
                fontWeight: '700',
                color: 'var(--wq-text-strong, #111827)',
                marginBottom: '4px',
              }}>
                {seasonShortLabel}
              </div>
              <div style={{
                fontSize: '14px',
                color: 'var(--wq-text-sub, #6B7280)',
              }}>
                {seasonLongLabel}
              </div>
            </div>

            {/* CTA 버튼 */}
            <button
              onClick={handleEventBubbleClick}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: 'var(--wq-accent-fill, #3182F6)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }}
            >
              {_maxDiscountCta}
            </button>
          </div>
        </>
      )}

      {/* 사이드 메뉴 배경 - 모바일 모드에서만 표시 */}
      {!isPcMode && isMenuOpen && (
        <div 
          onClick={() => {
            setIsMenuOpen(false);
          }} 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 999,
          }}
        />
      )}

      {/* 항상 사이드 메뉴 렌더링 */}
      {renderSideMenu()}
      
      {/* 로그인 팝업 */}
      <LoginPrompt 
        isOpen={showLoginPrompt}
        onClose={handleCloseLoginPrompt}
        feature="default"
      />
    </>
  );
};

export default Navigation; 